import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Check, ShoppingBag, ArrowLeft, ShieldCheck, Heart, Star, Camera, X } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { Stars } from "../components/Stars";
import { inr } from "../lib/format";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user, login } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reviewData, setReviewData] = useState({ reviews: [], average: 0, count: 0 });
  const [form, setForm] = useState({ rating: 5, body: "", photo: null });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviewData(r.data)).catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    loadReviews();
  }, [id]);

  const onReviewPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Please use an image under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitReview = async () => {
    if (!form.body.trim()) { toast.error("Please write a few words"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: id, rating: form.rating, title: "", body: form.body, photo: form.photo });
      toast.success("Thank you for your review!");
      setForm({ rating: 5, body: "", photo: null });
      loadReviews();
    } catch (e) {
      toast.error("Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
        <p className="font-serif text-2xl text-gold-muted">Saree not found</p>
        <Link to="/collection" className="text-gold mt-4 inline-block">Back to collection</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="min-h-[60vh] grid place-items-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  const saved = has(product.id);
  const headlineRating = reviewData.average || product.rating;
  const headlineCount = reviewData.count || product.review_count;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold mb-6" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="relative rounded-xl overflow-hidden gold-glow aspect-[4/5] sticky top-24 self-start">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
          <span className="absolute top-4 left-4 caption bg-obsidian/70 px-3 py-1 rounded">{product.category}</span>
        </motion.div>

        <div>
          <span className="caption">{product.occasion} · {product.fabric}</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-gold-light mt-3 leading-tight" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3" data-testid="pdp-rating">
            <Stars rating={headlineRating} size={16} />
            <span className="text-sm text-gold-muted">{headlineRating}</span>
            <a href="#reviews" className="text-sm text-muted-foreground hover:text-gold">· {headlineCount} reviews</a>
          </div>
          <p className="font-mono text-3xl text-gold mt-4" data-testid="product-price">{inr(product.price)}</p>

          <div className="hairline my-7" />

          <p className="text-muted-foreground leading-relaxed" data-testid="product-description">{product.description}</p>

          <div className="grid sm:grid-cols-2 gap-3 mt-7">
            {product.highlights.map((h) => (
              <div key={h} className="flex items-center gap-2 text-sm text-gold-muted">
                <Check className="w-4 h-4 text-gold shrink-0" /> {h}
              </div>
            ))}
          </div>

          <div className="glass rounded-lg p-4 mt-7 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gold-muted">Authenticity guaranteed</p>
              <p className="text-xs text-muted-foreground">Ships with a purity certificate · Free insured worldwide shipping</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button
              onClick={() => { addItem(product); }}
              data-testid="pdp-add-to-cart"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> Add to cart
            </button>
            <button
              onClick={() => navigate(`/try-on?product=${product.id}`)}
              data-testid="pdp-tryon-button"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold-muted py-3.5 rounded-full hover:bg-gold/10 transition-colors"
            >
              <Sparkles className="w-4 h-4 text-gold" /> Try this on me
            </button>
            <button
              onClick={() => toggle(product)}
              data-testid="pdp-wishlist-button"
              aria-label="Toggle wishlist"
              className="sm:w-14 inline-flex items-center justify-center gap-2 border border-gold/40 py-3.5 rounded-full hover:bg-gold/10 transition-colors"
            >
              <Heart className={`w-5 h-5 ${saved ? "fill-gold text-gold" : "text-gold-muted"}`} />
            </button>
          </div>

          <dl className="grid grid-cols-2 gap-y-3 gap-x-6 mt-9 text-sm">
            {[["Fabric", product.fabric], ["Colour", product.color], ["Zari", product.zari], ["Occasion", product.occasion]].map(([k, v]) => (
              <div key={k} className="flex flex-col border-b border-gold/10 pb-2">
                <dt className="caption text-[10px]">{k}</dt>
                <dd className="text-gold-muted mt-1">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Reviews */}
      <section id="reviews" className="mt-20 scroll-mt-24" data-testid="reviews-section">
        <div className="hairline mb-8" />
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <span className="caption">Loved by our customers</span>
            <h2 className="font-serif text-3xl text-gold-light mt-2">Reviews & ratings</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-serif text-5xl gold-text">{headlineRating}</span>
            <div>
              <Stars rating={headlineRating} size={16} />
              <p className="text-xs text-muted-foreground mt-1">{headlineCount} verified reviews</p>
            </div>
          </div>
        </div>

        {/* Write a review */}
        <div className="glass rounded-lg p-6 mb-8" data-testid="write-review">
          {user ? (
            <div>
              <p className="text-gold-muted mb-3">Share your experience</p>
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
                    <Star className={`w-6 h-6 ${n <= form.rating ? "fill-gold text-gold" : "text-gold/30"}`} />
                  </button>
                ))}
              </div>
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                data-testid="review-body-input"
                rows={3}
                placeholder="Tell others about the fabric, fit and drape…"
                className="w-full bg-obsidian-2 border border-gold/25 rounded-md p-3 text-sm text-gold-muted focus:outline-none focus:border-gold resize-none"
              />
              <div className="flex items-center gap-3 mt-3">
                {form.photo ? (
                  <div className="relative">
                    <img src={form.photo} alt="review" className="w-16 h-20 object-cover rounded-md gold-border" data-testid="review-photo-preview" />
                    <button onClick={() => setForm((f) => ({ ...f, photo: null }))} data-testid="review-photo-remove" className="absolute -top-2 -right-2 w-5 h-5 grid place-items-center rounded-full bg-obsidian text-destructive border border-gold/30">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label data-testid="review-photo-label" className="inline-flex items-center gap-2 text-sm text-gold-muted border border-gold/30 rounded-full px-4 py-2 cursor-pointer hover:bg-gold/10 transition-colors">
                    <Camera className="w-4 h-4 text-gold" /> Add a photo
                    <input type="file" accept="image/*" onChange={onReviewPhoto} className="hidden" data-testid="review-photo-input" />
                  </label>
                )}
                <button onClick={submitReview} disabled={submitting} data-testid="submit-review-button"
                  className="bg-gold text-obsidian font-semibold px-6 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
                  {submitting ? "Posting…" : "Post review"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">Sign in to write a review and share your look.</p>
              <button onClick={login} data-testid="review-signin-button" className="bg-gold text-obsidian font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors">Sign in</button>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {reviewData.reviews.map((r) => (
            <div key={r.review_id} className="glass rounded-lg p-5" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center gap-3">
                {r.avatar ? <img src={r.avatar} alt="" className="w-10 h-10 rounded-full object-cover gold-border" /> : <span className="w-10 h-10 rounded-full bg-secondary" />}
                <div>
                  <p className="text-sm text-gold-muted">{r.name}</p>
                  <Stars rating={r.rating} size={12} />
                </div>
              </div>
              {r.title ? <p className="font-serif text-lg text-gold-light mt-3">{r.title}</p> : null}
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.body}</p>
              {r.photo ? <img src={r.photo} alt="Customer" className="mt-3 w-24 h-28 object-cover rounded-md gold-border" /> : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
